// Which per-field ticks may be shown. A tick means the verification provider matched a value that
// was prefilled into the check from the profile, so only prefilled fields can ever carry one, and
// it is shown only while the check it came from counts as verified (passed and not expired). Every
// field the check returned is still listed; a field that was not prefilled is listed without a
// tick, whatever the check returned for it, and a document verified with nothing prefilled lists
// all of its fields without ticks.
//
// Written as plain CommonJS so the API server, which runs its files under plain node, can require
// this same file the browser bundle imports.

const { isVerifiedRecord, isCompanyVerified } = require('./verifiedStatus');

// The identity templates that prefill a person's details (kyc, kycTypeA, kycTypeB).
const PREFILLED_TEMPLATE_IDS = [
  'itmpl_1sX3MDy4E1K8Qgci56V237t4sbTG',
  'itmpl_ugRxCXUWudB9vKWRUGKe2pCRQ419',
  'itmpl_FJ8iKu8SASjKpNvtAX6fkKos5h97',
];

// Documents verified through one of those templates: U.S. licence, State ID, U.S. passport and
// passport card, permanent resident card, work permit, foreign passport, certificate of
// naturalization and certificate of citizenship.
const PREFILLED_DOCUMENT_TYPES = [
  'identity-1',
  'identity-2',
  'both-1',
  'both-1-ppc',
  'both-2',
  'both-4',
  'identity-13',
  'work-10',
  'work-11',
];

const PREFILLED_PERSON_FIELDS = ['name-first', 'name-last', 'address'];

const PREFILLED_COMPANY_FIELDS = [
  'business-name',
  'dba-name',
  'business-url',
  'business-type',
  'business-tax-identification-number',
];

const OWNER_FIELDS = [
  'name-first',
  'name-last',
  'email-address',
  'percentage-ownership',
  'birthdate',
  'social-security-number',
  'address',
];

const COMPANY_PERSON_FIELDS = ['name-first', 'name-last', 'email-address', 'job-title', 'percentage-ownership'];

const COMPANY_PERSON_PREFIXES = ['control-person-2-', 'control-person-', 'form-filler-'];

const isPlainObject = value => !!value && typeof value === 'object' && !Array.isArray(value);

// An owner's or company person's field key, e.g. `ubo-1-name-first` or `control-person-job-title`.
const isPrefilledCompanyPersonKey = key => {
  if (typeof key !== 'string') return false;
  const owner = key.match(/^ubo-\d+-(.+)$/);
  if (owner) return OWNER_FIELDS.includes(owner[1]);
  const prefix = COMPANY_PERSON_PREFIXES.find(p => key.startsWith(p));
  return !!prefix && COMPANY_PERSON_FIELDS.includes(key.slice(prefix.length));
};

// Every key of the object, keeping a value only where `tickable` allows it and false elsewhere.
const markKeys = (object, tickable) =>
  Object.keys(object).reduce((marked, key) => {
    marked[key] = tickable(key) ? object[key] : false;
    return marked;
  }, {});

// A person's stored ticks: every field kept, but only prefilled fields may keep a tick. Pass
// `prefilled` false for a document checked with nothing prefilled, so none of its fields tick.
const personTicks = (checks, prefilled = true) =>
  isPlainObject(checks)
    ? markKeys(checks, key => prefilled && PREFILLED_PERSON_FIELDS.includes(key))
    : {};

const unverified = entry =>
  isPlainObject(entry) && 'isVerified' in entry ? Object.assign({}, entry, { isVerified: false }) : entry;

const isPrefilledCompanyField = fieldName => PREFILLED_COMPANY_FIELDS.includes(fieldName);
const never = () => false;

const markedEntries = (entries, tickable) =>
  Array.isArray(entries) ? entries.map(entry => (tickable(entry?.fieldName) ? entry : unverified(entry))) : [];

const withMarkedFields = (people, tickable) =>
  Array.isArray(people)
    ? people.map(person =>
        Object.assign({}, person, {
          fields: isPlainObject(person?.fields) ? markKeys(person.fields, tickable) : {},
        })
      )
    : [];

const BUSINESS_DETAIL_CATEGORIES = ['businessIdentity', 'businessRegistry', 'businessOther'];

// Older business details were stored as one flat map of field name to its tick rather than in
// categories; they are kept in that shape.
const isFlatBusinessDetails = details =>
  Object.keys(details).length > 0 && !BUSINESS_DETAIL_CATEGORIES.some(category => category in details);

const markedCompany = (kyb, ticksAllowed) => {
  if (!isPlainObject(kyb)) return null;
  const companyField = ticksAllowed ? isPrefilledCompanyField : never;
  const personField = ticksAllowed ? isPrefilledCompanyPersonKey : never;
  const details = isPlainObject(kyb.businessDetails) ? kyb.businessDetails : {};
  return {
    businessDetails: isFlatBusinessDetails(details)
      ? markKeys(details, companyField)
      : {
          businessIdentity: markedEntries(details.businessIdentity, companyField),
          businessRegistry: markedEntries(details.businessRegistry, companyField),
          businessOther: markedEntries(details.businessOther, companyField),
        },
    beneficialOwners: withMarkedFields(kyb.beneficialOwners, personField),
    authorizedPersons: withMarkedFields(kyb.authorizedPersons, personField),
    insuranceAndReports: Array.isArray(kyb.insuranceAndReports) ? kyb.insuranceAndReports.map(unverified) : [],
  };
};

// A company's stored ticks: every entry and field kept, but only prefilled company, owner and
// company-person fields may keep a tick. Insurance and report entries never tick.
const companyTicks = kyb => markedCompany(kyb, true);

// The ticks a document's fields may show, given its newest verification record.
const documentTicks = (record, checks) =>
  isVerifiedRecord(record)
    ? personTicks(checks, PREFILLED_DOCUMENT_TYPES.includes(record.documentType))
    : {};

// The company ticks that may be shown, or null while the company is not verified.
const companyTicksFor = (records, kyb) => (isCompanyVerified(records) ? companyTicks(kyb) : null);

// The company's details as its owner sees them: every entry is listed while a check is under way
// or has failed, but ticks show only while the company counts as verified.
const companyDisplayTicks = (records, kyb) => markedCompany(kyb, isCompanyVerified(records));

// Whether one company detail may show a tick.
const isCompanyFieldTicked = (records, kyb, fieldName) => {
  if (!PREFILLED_COMPANY_FIELDS.includes(fieldName)) return false;
  const ticks = companyTicksFor(records, kyb);
  if (!ticks) return false;
  const details = ticks.businessDetails;
  if (isFlatBusinessDetails(details)) return details[fieldName] === true;
  return Object.values(details).some(entries =>
    entries.some(entry => entry.fieldName === fieldName && entry.isVerified === true)
  );
};

module.exports = {
  PREFILLED_TEMPLATE_IDS,
  PREFILLED_DOCUMENT_TYPES,
  PREFILLED_PERSON_FIELDS,
  PREFILLED_COMPANY_FIELDS,
  isPrefilledCompanyPersonKey,
  personTicks,
  companyTicks,
  documentTicks,
  companyTicksFor,
  companyDisplayTicks,
  isCompanyFieldTicked,
};
